import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { getVehicleOwnerRequirements, setVehicleOwnerRequirement, getVehicleOwnerRequirementsAuditLog } from '../api/admin';

// Vehicle Owner Onboarding v2.1 — Section 4.1 "Configurable Country Rule
// Engine": which fields/documents are required to approve a vehicle-owner
// application, per country + account_type. Editable here with no code change
// or redeploy — read live by backend/src/lib/verification.js
// getRequiredFieldKeys() / computeOwnerProgress() / canPublishVehicle().
const REQUIREMENT_LABEL = {
  full_name: 'Full legal name',
  date_of_birth: 'Date of birth',
  home_address: 'Residential address',
  state_region: 'State / County / Region',
  id_document: 'Identity document (+ selfie/liveness)',
  vehicle_registration: 'Vehicle registration / vehicle documentation',
  ownership_proof: 'Proof of ownership or authorization to rent out',
  insurance_proof: 'Valid proof of insurance',
  insurance: 'Valid proof of insurance',
  company_name: 'Official company name',
  legal_form: 'Legal form',
  business_address: 'Registered business address',
  company_registration_number: 'Company registration number / tax ID',
  tax_id: 'Tax ID / country-specific tax identifier',
  rep_name: 'Authorized representative details',
  rep_authorization: 'Proof of authorization (representative)',
  business_registration: 'Business / trade license proof',
};

const PRIVATE_KEYS = ['full_name', 'date_of_birth', 'home_address', 'state_region', 'id_document', 'vehicle_registration', 'ownership_proof', 'insurance_proof'];
const DRIVER_KEYS = ['full_name', 'date_of_birth', 'home_address', 'state_region', 'id_document', 'vehicle_registration', 'insurance_proof'];
const COMPANY_KEYS = ['company_name', 'legal_form', 'business_address', 'state_region', 'company_registration_number', 'tax_id', 'rep_name', 'rep_authorization', 'business_registration', 'insurance_proof'];

const COUNTRY_NAMES = { KE: 'Kenya', US: 'United States' };
const CountryHeader = ({ code }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
    <img src={`https://flagcdn.com/${code.toLowerCase()}.svg`} alt="" style={{ width: 18, height: 13, objectFit: 'cover', borderRadius: 2, boxShadow: '0 0 0 1px rgba(0,0,0,0.08)' }} />
    {COUNTRY_NAMES[code] || code}
  </span>
);

function RequirementGrid({ rowKeys, countries, values, savingKey, onToggle }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 420, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>
        <thead>
          <tr>
            <th style={th}></th>
            {countries.map((c) => <th key={c} style={{ ...th, textAlign: 'center' }}><CountryHeader code={c} /></th>)}
          </tr>
        </thead>
        <tbody>
          {rowKeys.map((key) => (
            <tr key={key}>
              <td style={td}>{REQUIREMENT_LABEL[key] || key}</td>
              {countries.map((country) => {
                const cellKey = `${country}:${key}`;
                return (
                  <td key={country} style={{ ...td, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!values[country]?.[key]}
                      disabled={savingKey === cellKey}
                      onChange={() => onToggle(country, key)}
                      style={{ width: 18, height: 18, cursor: 'pointer' }}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function VehicleOwnerRequirements() {
  const [countries] = useState(['US', 'KE']);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getVehicleOwnerRequirements();
      setRows(res.data.requirements || []);
      setNote(res.data.note || '');
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load vehicle-owner requirements.');
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLog = async () => {
    setAuditLoading(true);
    try {
      const res = await getVehicleOwnerRequirementsAuditLog();
      setAuditLog(res.data.entries || []);
    } catch (e) {
      // Non-fatal — the rule grid above is the primary feature.
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => { load(); loadAuditLog(); }, []);

  // Country-wide rows only (region IS NULL) — region-specific overrides exist
  // in the data model but aren't editable from this simple grid yet.
  const valuesFor = (accountType) => {
    const map = {};
    for (const country of countries) map[country] = {};
    for (const row of rows) {
      if (row.account_type !== accountType || row.region) continue;
      if (!map[row.country_code]) map[row.country_code] = {};
      map[row.country_code][row.requirement_key] = row.is_required;
    }
    return map;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const privateValues = useMemo(() => valuesFor('private_individual'), [rows]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const companyValues = useMemo(() => valuesFor('company_fleet'), [rows]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const driverValues = useMemo(() => valuesFor('driver'), [rows]);

  const toggle = async (accountType, country, key) => {
    // Each section reads its own values (the Driver section used to read the
    // company column, so its checkboxes saved the wrong value).
    const current = accountType === 'private_individual' ? privateValues : accountType === 'driver' ? driverValues : companyValues;
    const next = !current[country]?.[key];
    const cellKey = `${accountType}:${country}:${key}`;
    setSavingKey(cellKey);
    try {
      const res = await setVehicleOwnerRequirement(country, accountType, null, key, next);
      setRows((prev) => {
        const idx = prev.findIndex((r) => r.country_code === country && r.account_type === accountType && !r.region && r.requirement_key === key);
        if (idx === -1) return [...prev, res.data.requirement];
        const copy = [...prev];
        copy[idx] = res.data.requirement;
        return copy;
      });
      toast.success('Saved');
      loadAuditLog();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not update requirement');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800 }}>Vehicle Lessor Requirements</h2>
      <p style={{ margin: '0 0 24px', color: '#64748b', fontSize: 14, maxWidth: 760 }}>
        Which details and documents are required, per country and account type. They are used live: the registration
        shows exactly these items, an application can only be submitted and approved when they are complete, and the
        admin review shows each item as done or missing. Private Individual and Company / Fleet apply to the vehicle
        lessor application; Driver applies to the driver and boda-boda / scooter registration.
      </p>

      {error && <p style={{ color: '#b91c1c' }}>{error}</p>}
      {note && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 14 }}>
          ⚠️ {note}
        </div>
      )}

      <h3 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 700 }}>Private Individual</h3>
      {loading ? <p>Loading…</p> : (
        <RequirementGrid
          rowKeys={PRIVATE_KEYS} countries={countries} values={privateValues}
          savingKey={savingKey && savingKey.startsWith('private_individual:') ? savingKey.split(':').slice(1).join(':') : null}
          onToggle={(country, key) => toggle('private_individual', country, key)}
        />
      )}

      <h3 style={{ margin: '32px 0 4px', fontSize: 16, fontWeight: 700 }}>Driver</h3>
      <p style={{ margin: '0 0 10px', color: '#64748b', fontSize: 13 }}>Driver and boda-boda / scooter registration. Identity document includes the live selfie; the vehicle registration is the NTSA document in Kenya. The driving licence (and PSV proof in Kenya) and the inspection report are always required for driving.</p>
      {loading ? <p>Loading…</p> : (
        <RequirementGrid
          rowKeys={DRIVER_KEYS} countries={countries} values={driverValues}
          savingKey={savingKey && savingKey.startsWith('driver:') ? savingKey.split(':').slice(1).join(':') : null}
          onToggle={(country, key) => toggle('driver', country, key)}
        />
      )}

      <h3 style={{ margin: '32px 0 10px', fontSize: 16, fontWeight: 700 }}>Company / Fleet Provider</h3>
      {loading ? <p>Loading…</p> : (
        <RequirementGrid
          rowKeys={COMPANY_KEYS} countries={countries} values={companyValues}
          savingKey={savingKey && savingKey.startsWith('company_fleet:') ? savingKey.split(':').slice(1).join(':') : null}
          onToggle={(country, key) => toggle('company_fleet', country, key)}
        />
      )}

      <h3 style={{ margin: '32px 0 10px', fontSize: 16, fontWeight: 700 }}>Recent Changes</h3>
      <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: 13 }}>
        Every save above is logged here with who made it, when, and the before/after value.
      </p>
      <AuditLogPanel entries={auditLog} loading={auditLoading} />
    </div>
  );
}

function AuditLogPanel({ entries, loading }) {
  if (loading) return <p>Loading…</p>;
  if (entries.length === 0) {
    return <p style={{ color: '#94a3b8', fontSize: 14 }}>No changes logged yet.</p>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 640, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>
        <thead>
          <tr>
            <th style={th}>When</th>
            <th style={th}>Admin</th>
            <th style={th}>Country</th>
            <th style={th}>Account type</th>
            <th style={th}>Requirement</th>
            <th style={{ ...th, textAlign: 'center' }}>Change</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id}>
              <td style={td}>{new Date(e.changed_at).toLocaleString()}</td>
              <td style={td}>{e.changed_by_name || '—'}</td>
              <td style={td}><CountryHeader code={e.country_code} /></td>
              <td style={td}>{e.account_type === 'company_fleet' ? 'Company / Fleet' : e.account_type === 'driver' ? 'Driver' : 'Private Individual'}</td>
              <td style={td}>{REQUIREMENT_LABEL[e.requirement_key] || e.requirement_key}</td>
              <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                <ValuePill value={e.old_value} /> → <ValuePill value={e.new_value} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ValuePill({ value }) {
  if (value === null || value === undefined) return <span style={{ color: '#94a3b8' }}>(new)</span>;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700,
      background: value ? '#dcfce7' : '#fee2e2', color: value ? '#166534' : '#991b1b',
    }}>
      {value ? 'Required' : 'Not required'}
    </span>
  );
}

const th = { textAlign: 'left', padding: '10px 14px', borderBottom: '2px solid #e2e8f0', fontSize: 13, color: '#334155', fontWeight: 700 };
const td = { padding: '10px 14px', borderBottom: '1px solid #f1f5f9', fontSize: 14, color: '#0f172a' };
